// Editable · JsonTextArea
//
// TextArea for jsonb-backed fields. Accepts the HYDRATED column value —
// object / array / number / string — and renders non-strings pretty-printed;
// emits the RAW textarea string on change. The asymmetry is deliberate:
// emitting parsed values on every keystroke would re-stringify (and reformat)
// the text under the caret, so the parse back to a jsonb-ready value happens
// once at submit (`@ds/forms/parseJsonFieldValues`, applied in SchemaForm's submit pipeline). A jsonb
// column holding a plain string round-trips bare (no added quotes) — the
// legacy `appConfigDao.setKey` semantic.
//
// Works in both widget contexts (see `fieldTypeRegistry`): AntD Form.Item
// injects `value` via cloneElement (form context), inline-edit wires it
// manually — the object→text mapping lives HERE so both get it.

import type React from "react";
import { TextArea } from "@ds/editable/TextArea";

// ─── Inputs (data props — the model) ─────────────────────────────────────────

export interface JsonTextAreaInput {
  /** Hydrated jsonb value (object/array/…) or the raw string being typed. */
  value?: unknown;
  placeholder?: string;
  disabled?: boolean;
  /** Number of visible text rows. Default 4. */
  rows?: number;
  /** Auto-focus on mount — for inline-edit affordances. */
  autoFocus?: boolean;
  /** Validation status — passes through to AntD's status prop. */
  status?: "" | "warning" | "error";
  /** `id` forwarded for Form.Item label association. */
  id?: string;
  /** `data-testid` on the inner <textarea> (reachable + fillable). */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ──────────────────────────────

export interface JsonTextAreaOutput {
  /** Emits the raw textarea string — parse at submit, not per keystroke. */
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export type JsonTextAreaProps = JsonTextAreaInput & JsonTextAreaOutput;

/** Non-strings render pretty-printed; strings pass through untouched so
 *  typing never round-trips through JSON.parse/stringify. (Internal — a
 *  helper export would break react-refresh for the component file.) */
function jsonValueToText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
}

// ─── Component ────────────────────────────────────────────────────────────────

export function JsonTextArea({
  value,
  placeholder,
  disabled,
  rows = 4,
  autoFocus,
  status,
  id,
  testId,
  onChange,
  onBlur,
  onKeyDown,
}: JsonTextAreaProps) {
  return (
    <TextArea
      value={jsonValueToText(value)}
      placeholder={placeholder}
      disabled={disabled}
      rows={rows}
      autoFocus={autoFocus}
      status={status}
      id={id}
      testId={testId}
      onChange={onChange}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  );
}
